import { useQuery } from '@tanstack/react-query';
import type { CodeEnum } from '@zelos/codegen/apis';
import { V1Service } from '@zelos/codegen/apis';
import { useMemo } from 'react';

const cacheTime = 60 * 60 * 1000; // 한시간

const languageNamesMap = Object.freeze({
  pccp: ['Python3', 'C++', 'Java', 'JavaScript', 'C', 'C#'],
  pcce: ['Python3', 'C++', 'Java'],
  pcsql: ['MySQL', 'Oracle'],
});

export const useCommQueryData = () => {
  const { data: classificationList } = useQuery(
    ['v1ClassficationsList'],
    async () => {
      const data = await V1Service.v1ClassficationsList();
      data.sort((a) => (a.code === 'PCCP' ? -1 : 0));

      return data;
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      cacheTime,
    },
  );

  const { data: languageList } = useQuery(
    ['v1ProgrammingLanguagesList'],
    V1Service.v1ProgrammingLanguagesList,
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      cacheTime,
    },
  );

  const { data: rulesList } = useQuery(
    ['v1RulesList'],
    async () => {
      const data = await V1Service.v1RulesList();
      data.sort((a, b) => a.id - b.id);

      return data;
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      cacheTime,
    },
  );

  const pccpLanguageList = useMemo(
    () => (languageList ?? []).filter(({ language }) => language && languageNamesMap.pccp.includes(language)),
    [languageList],
  );

  const pcceLanguageList = useMemo(
    () => (languageList ?? []).filter(({ language }) => language && languageNamesMap.pcce.includes(language)),
    [languageList],
  );

  const pcsqlLanguageList = useMemo(
    () => (languageList ?? []).filter(({ language }) => language && languageNamesMap.pcsql.includes(language)),
    [languageList],
  );

  const findClassification = (classificationId?: number | null) =>
    classificationList?.find(({ id }) => id === classificationId);

  const findClassificationByCode = (classificationCode: CodeEnum) =>
    classificationList?.find(({ code }) => code === classificationCode);

  const findLanguage = (language?: number | null) =>
    languageList?.find(({ id }) => language === id);

  const findLanguageByIncludeName = (languageName?: string | null) =>
    languageName ? languageList?.find(({ language }) => language?.toLowerCase().includes(languageName.toLowerCase())) : undefined;

  const findLanguageNames = (languages?: number[] | null) =>
    languageList?.filter(({ id }) => !!languages?.find((_id) => id === _id))?.map(({ language }) => language);

  const findRule = (rule: number) =>
    rulesList?.find(({ id }) => rule === id);

  return {
    classificationList,
    languageList,
    pccpLanguageList,
    pcceLanguageList,
    pcsqlLanguageList,
    rulesList,
    findClassification,
    findClassificationByCode,
    findLanguage,
    findLanguageNames,
    findLanguageByIncludeName,
    findRule,
  };
};
